import { type FeatureFlagAPI, FeatureFlagRequester } from '@cobliteam/api-lib/src/dashboard/featureFlag';
import { Environment } from '@cobliteam/react-environments';
import { camelCase, kebabCase } from 'change-case';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import type FeatureFlags from './featureFlags';
import { featureFlags } from './featureFlags';

const env = Environment.getInstance().getEnvironment();
type LaunchDarklyFlags = Record<string, unknown>;
type FeatureFlagKey = keyof FeatureFlags;

// Register a feature flag for the current user - /users/feature-flag/{flagKey}/register
type RegisterFunction = (flagKey: FeatureFlagKey) => Promise<FeatureFlagAPI | undefined>;

// Unregister a feature flag for the current user - /users/feature-flag/{flagKey}/unregister
type UnregisterFunction = (flagKey: FeatureFlagKey) => Promise<FeatureFlagAPI | undefined>;
type UseFeatureFlagsResult = FeatureFlags & { register: RegisterFunction; unregister: UnregisterFunction };

const createFeatureFlagRequester = () => new FeatureFlagRequester({ host: env.apiHost });

const register: RegisterFunction = async (flagKey) => createFeatureFlagRequester().register(kebabCase(flagKey));
const unregister: UnregisterFunction = async (flagKey) => createFeatureFlagRequester().unregister(kebabCase(flagKey));

/** Resolve a LaunchDarkly flag value when SDK keys differ from our catalog (raw vs camelCase, etc.). */
const resolveLdFlagValue = (flags: LaunchDarklyFlags, catalogKey: string, fallback: unknown): unknown => {
  if (Object.hasOwn(flags, catalogKey)) {
    return flags[catalogKey];
  }

  const normalizedCatalogKey = camelCase(catalogKey);
  if (normalizedCatalogKey !== catalogKey && Object.hasOwn(flags, normalizedCatalogKey)) {
    return flags[normalizedCatalogKey];
  }

  for (const ldKey of Object.keys(flags)) {
    if (ldKey === catalogKey || camelCase(ldKey) === normalizedCatalogKey) {
      return flags[ldKey];
    }
  }

  return fallback;
};

const resolveFeatureFlags = (flags: LaunchDarklyFlags): FeatureFlags => {
  const resolvedEntries = (Object.entries(featureFlags) as [FeatureFlagKey, FeatureFlags[FeatureFlagKey]][]).map(([key, fallback]) => [
    key,
    resolveLdFlagValue(flags, key, fallback),
  ]);

  return Object.fromEntries(resolvedEntries) as FeatureFlags;
};

const useFeatureFlags = (): UseFeatureFlagsResult => {
  const ldFlags = useFlags() as LaunchDarklyFlags;
  const resolvedFlags = resolveFeatureFlags(ldFlags);

  return useMemo(
    () => ({
      ...resolvedFlags,
      register,
      unregister,
    }),
    [resolvedFlags]
  );
};

export { featureFlags };
export type { FeatureFlags };
export default useFeatureFlags;
